import { useContext } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import { get } from 'lodash';

import Steven from 'helpers/steven';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import TASK_MARK_COMPLETE from 'components/projects/taskList/gql/taskMarkComplete.graphql';
import TASK_MARK_INCOMPLETE from 'components/projects/taskList/gql/taskMarkIncomplete.graphql';
import GET_TASK_LIST_HEADER_INFO from 'components/projects/taskList/gql/getTaskListHeaderInfo.graphql';
import TemplateContext from 'components/projects/taskList/templates/TemplateContext';
import toast from 'helpers/toast';

defineMessages({
  taskStatusUpdateError: {
    id: 'Projects.taskList.task.taskStatusUpdateError',
    defaultMessage: 'Error updating field status',
    description:
      'message displayed when an error occurs updating a task status',
  },
  taskCompleted: {
    id: 'Projects.taskList.task.taskCompleted',
    defaultMessage: 'Field Completed',
    description: 'message displayed when a task status is marked complete'
  },
  taskCompleteUndo: {
    id: 'Projects.taskList.task.taskCompleteUndo',
    defaultMessage: 'Undo',
    description: 'button label displayed when completing task to undo'
  }
});

const useTaskStatus = ({ taskId, taskListId, skipOptimisticUpdate = false }) => {
  const { formatMessage } = useIntl();
  const { template } = useContext(TemplateContext) || {};
  const { cache } = useApolloClient();

  const showErrorToast = (errorMsg) => {
    toast.error(errorMsg);
  };

  const [markTaskIncompleteMutation] = useMutation(TASK_MARK_INCOMPLETE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);

      if (gqlErrors.length) {
        showErrorToast(gqlErrors[0].message);
      }
    },
    optimisticResponse: () => {
      const { viewer } = cache.readQuery({
        query: GET_TASK_LIST_HEADER_INFO,
        variables: {
          id: taskListId,
        },
        skip: !taskListId,
      });

      const tasksCount = get(viewer, 'taskList.tasksCount', 0);
      const tasksCompletedCount = get(viewer, 'taskList.tasksCompletedCount', -1);

      return {
        __typename: 'Mutation',
        taskMarkIncomplete: {
          __typename: 'TaskMarkIncompletePayload',
          task: {
            id: taskId,
            __typename: 'Task',
            completed: false
          },
          taskList: {
            __typename: 'TaskList',
            id: taskListId,
            tasksCount,
            tasksCompletedCount: Math.max(tasksCompletedCount - 1, 0), // prevent negative count
          },
          errors: [],
        },
      };
    },
    onError: () => {
      showErrorToast(
        formatMessage({ id: 'Projects.taskList.task.taskStatusUpdateError' }),
      );
    },
  });

  const [markTaskCompletedMutation] = useMutation(TASK_MARK_COMPLETE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);
      const taskDbId = graphQLIdDecoder(taskId)?.databaseId;

      if (gqlErrors.length) {
        showErrorToast(gqlErrors[0].message);
        return;
      }
      if (taskDbId) {
        Steven.entityCrud({
          entity_id: taskDbId,
          entity_type: 'checklist_field',
          action: 'updated',
          updated_action: 'status_marked_completed'
        });
      }
      
      if (skipOptimisticUpdate) {
        toast.success(
          formatMessage({ id: 'Projects.taskList.task.taskCompleted' }),
          formatMessage({ id: 'Projects.taskList.task.taskCompleteUndo' }),
          async () => {
            await markTaskIncompleteMutation({
              variables: {
                input: {
                  id: taskId,
                },
              },
            });
          },
          6
        );
      }
    },
    optimisticResponse: () => {
      const { viewer } = cache.readQuery({
        query: GET_TASK_LIST_HEADER_INFO,
        variables: {
          id: taskListId,
        },
        skip: !taskListId
      });
    
      const tasksCount = get(viewer, 'taskList.tasksCount', 0);
      const tasksCompletedCount = get(viewer, 'taskList.tasksCompletedCount', -1);
    
      return {
        __typename: 'Mutation',
        taskMarkComplete: {
          __typename: 'TaskMarkCompletePayload',
          task: {
            id: taskId,
            __typename: 'Task',
            completed: !skipOptimisticUpdate,
          },
          taskList: {
            __typename: 'TaskList',
            id: taskListId,
            tasksCount,
            tasksCompletedCount: tasksCompletedCount + 1, // Will be 0 if taskList.taskCompletedCount is null
          },
          errors: [],
        }
      }
    },
    onError: () => {
      showErrorToast(formatMessage({ id: 'Projects.taskList.task.taskStatusUpdateError' }));
    },
  });

  const updateTaskStatus = async (task) => {
    if (template || !task.id) return;

    if (!task.completed) {
      await markTaskCompletedMutation({
        variables: {
          input: {
            id: task.id,
          },
        },
      });
    } else {
      await markTaskIncompleteMutation({
        variables: {
          input: {
            id: task.id,
          },
        },
      });
    }
  };

  return {
    toggleTaskStatus: updateTaskStatus,
  };
};

export default useTaskStatus;
